/**
 * Клиент мастера онбординга (CTHS-216/217, эпик CTHS-157).
 *
 * Контракт backend (CTHS-215 + ревизия 17.09.2026, см.
 * `docs/features/2026-09-17_onboarding-wizard-web/api.md`):
 *   GET   /api/onboarding/state       → OnboardingState (всегда 200, даже если мастер не начинали)
 *   GET   /api/onboarding/steps       → OnboardingStepMeta[] (набор и порядок шагов задаёт backend)
 *   PATCH /api/onboarding/step/{n}    ← { answer } → OnboardingState
 *   POST  /api/onboarding/complete    → OnboardingState
 *   POST  /api/onboarding/skip        → OnboardingState
 *
 * Состояние кэшируется в модуле: главная спрашивает его на каждом монтировании, и без кэша
 * запрос уходил бы при каждом возврате на `/home`.
 */
import createAxiosInstance from './AxiosSupport';

export type OnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'SKIPPED';

export interface OnboardingState {
    /** Есть ли сохранённый прогресс: `false` — мастер ещё не открывали. */
    configured: boolean;
    status: OnboardingStatus;
    /** Номер последнего сохранённого слота; 0/`null`, пока ни один шаг не сохранён. */
    lastStep: number | null;
    /** Ответы шагов: ключ — номер слота строкой. */
    stepAnswers: Record<string, unknown>;
    /** Сколько шагов в мастере (знаменатель прогресса). */
    totalSteps: number;
    /** Слот, с которого мастер возобновляется. */
    nextStep: number;
}

export interface OnboardingStepMeta {
    number: number;
    code: string;
    order: number;
    skippable: boolean;
}

/** Мастер уже пройден или отклонён — показывать его повторно нельзя. */
export const isOnboardingClosed = (state: OnboardingState): boolean =>
    state.status === 'COMPLETED' || state.status === 'SKIPPED';

let cachedState: OnboardingState | null = null;

/** Сбрасывает кэш состояния: после сохранения шага, пропуска и завершения. */
export const invalidateOnboardingState = (): void => {
    cachedState = null;
};

const remember = (state: OnboardingState): OnboardingState => {
    cachedState = state;
    return state;
};

export async function fetchOnboardingState(force = false): Promise<OnboardingState> {
    if (!force && cachedState) {
        return cachedState;
    }
    const api = await createAxiosInstance();
    const res = await api.get<OnboardingState>('/onboarding/state');
    return remember(res.data);
}

export async function fetchOnboardingSteps(): Promise<OnboardingStepMeta[]> {
    const api = await createAxiosInstance();
    const res = await api.get<OnboardingStepMeta[]>('/onboarding/steps');
    return res.data;
}

export async function saveOnboardingStep(stepNumber: number, answer: unknown): Promise<OnboardingState> {
    const api = await createAxiosInstance();
    const res = await api.patch<OnboardingState>(`/onboarding/step/${stepNumber}`, {answer});
    return remember(res.data);
}

export async function completeOnboarding(): Promise<OnboardingState> {
    const api = await createAxiosInstance();
    const res = await api.post<OnboardingState>('/onboarding/complete');
    return remember(res.data);
}

export async function skipOnboarding(): Promise<OnboardingState> {
    const api = await createAxiosInstance();
    const res = await api.post<OnboardingState>('/onboarding/skip');
    return remember(res.data);
}
